import { useEffect, useState } from "react";

import {
  Copy,
  ExternalLink,
  Globe,
  History,
  Layers,
  Loader2,
  MoreVertical,
  Pencil,
  Play,
  Trash2,
} from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { TableCell, TableRow } from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import { ChannelChips } from "./channel-icon";
import {
  Broadcast,
  BroadcastRun,
  formatCronSchedule,
  isHttpUrl,
  relativeTime,
  runAt,
  broadcastsApi,
  isAutoPaused,
} from "./use-broadcasts";

interface BroadcastRowProps {
  broadcast: Broadcast;
  /**
   * Where the row is rendered. In `project` scope a broadcast that also captures OTHER projects is
   * read-only: editing it here would change what those projects' owners receive.
   */
  scope?: "workspace" | "project";
  /** Resolves false when the engine refused the trigger, so the row can re-enable at once. */
  onRunNow: (broadcast: Broadcast) => Promise<boolean>;
  onTogglePause: (broadcast: Broadcast) => Promise<void>;
  onDelete: (broadcast: Broadcast) => Promise<void>;
  onEdit: (broadcast: Broadcast) => void;
  /** Creates a paused copy (same projects, schedule and channels). */
  onDuplicate: (broadcast: Broadcast) => Promise<void>;
  onOpenHistory: (broadcast: Broadcast) => void;
}

type StatusTone = "ok" | "fail" | "warn" | "idle" | "capturing";

/** A broadcast's effective sources: `sources` when it is multi-source, else the single `source`. */
function sourceCount(broadcast: Broadcast): number {
  return broadcast.sources?.length || 1;
}

const TONE_DOT: Record<StatusTone, string> = {
  ok: "bg-emerald-500",
  fail: "bg-red-500",
  warn: "bg-amber-500",
  idle: "bg-muted-foreground/40",
  capturing: "bg-primary",
};
const TONE_TEXT: Record<StatusTone, string> = {
  ok: "text-emerald-600 dark:text-emerald-400",
  fail: "text-red-600 dark:text-red-400",
  warn: "text-amber-600 dark:text-amber-400",
  idle: "text-muted-foreground",
  capturing: "text-primary",
};

/** Status-cell poll cadence, and the faster one used while waiting for a new run row. */
const RUN_POLL_MS = 3000;
const PENDING_POLL_MS = 1000;
/** Give up waiting for a triggered run to appear, so the button can never stick. */
const RUN_PENDING_GRACE_MS = 20000;

/** A Test-send accepted by the engine whose run row has not shown up yet. */
interface PendingRun {
  since: number;
  /** Latest run at click time — anything newer than this is our run. */
  afterRunId: string | null;
}

/**
 * A run still doing work — capturing, or with a delivery not yet settled. Drives
 * both the poller and the Test-send button: the engine refuses a second run while
 * one is in flight (409), so the button must not invite the click.
 */
function runInFlight(run: BroadcastRun | null): boolean {
  return (
    !!run &&
    (run.status === "pending" ||
      run.status === "capturing" ||
      (run.deliveries ?? []).some(
        (d) => d.status === "queued" || d.status === "pending",
      ))
  );
}

function summarizeRun(run: BroadcastRun | null): {
  tone: StatusTone;
  label: string;
  detail: string;
} {
  if (!run) return { tone: "idle", label: "No runs yet", detail: "" };
  // `pending` = the engine accepted the run but no worker slot is free yet — show it, so a
  // queued test send never looks like "nothing happened".
  if (run.status === "pending") {
    return { tone: "capturing", label: "Queued…", detail: "" };
  }
  if (run.status === "capturing") {
    return { tone: "capturing", label: "Capturing…", detail: "" };
  }
  const deliveries = run.deliveries ?? [];
  const total = deliveries.length;
  const ok = deliveries.filter((d) => d.status === "delivered").length;
  const failed = deliveries.filter((d) => d.status === "failed").length;
  const pending = total - ok - failed;
  const when = relativeTime(runAt(run));
  if (run.status === "failed") {
    // The text itself lives in Run history (full width, copyable) — here only when.
    return { tone: "fail", label: "Failed", detail: when };
  }
  if (total === 0) {
    return { tone: "ok", label: "Captured", detail: when };
  }
  if (pending > 0) {
    // Some deliveries still in flight — keep the spinner (poller stays active).
    return { tone: "capturing", label: "Delivering…", detail: `${ok}/${total} sent` };
  }
  if (ok === 0) {
    // Everything failed → Failed, not Partial.
    return { tone: "fail", label: "Failed", detail: `${when} · 0/${total}` };
  }
  if (ok < total) {
    return { tone: "warn", label: "Partial", detail: `${when} · ${ok}/${total}` };
  }
  return { tone: "ok", label: "Delivered", detail: `${when} · ${ok}/${total}` };
}

/** "Weekdays 09:00 · Asia/Saigon" split into the cadence line and its timezone. */
function scheduleParts(cron: string, tz: string): { line: string; zone: string } {
  const formatted = formatCronSchedule(cron, tz);
  const suffix = ` · ${tz}`;
  return formatted.endsWith(suffix)
    ? { line: formatted.slice(0, -suffix.length), zone: tz }
    : { line: formatted, zone: tz };
}

/** Compact "what this captures" line under the name. */
function sourceMeta(broadcast: Broadcast): { icon: "layers" | "globe"; text: string } {
  const count = sourceCount(broadcast);
  if (count > 1) return { icon: "layers", text: `${count} projects` };
  if (broadcast.source.type === "url") {
    let host = broadcast.source.url ?? "URL";
    try {
      host = new URL(broadcast.source.url ?? "").host || host;
    } catch {
      /* keep the raw value */
    }
    return { icon: "globe", text: host };
  }
  return { icon: "layers", text: "1 project" };
}

export function BroadcastRow({
  broadcast,
  onRunNow,
  onTogglePause,
  onDelete,
  onEdit,
  onDuplicate,
  onOpenHistory,
  scope = "workspace",
}: BroadcastRowProps): JSX.Element {
  const [running, setRunning] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [latestRun, setLatestRun] = useState<BroadcastRun | null>(null);
  const [pending, setPending] = useState<PendingRun | null>(null);

  // Load the most recent run for the status cell, then poll while it's still
  // capturing or a delivery is queued so it settles to Delivered/Failed live.
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;
    const stop = (): void => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };
    const load = async (): Promise<BroadcastRun | null> => {
      const { items } = await broadcastsApi.getRuns(broadcast.id, { limit: 1 });
      const latest = items[0] ?? null;
      if (!cancelled) setLatestRun(latest);
      return latest;
    };

    /**
     * Nothing left to watch? Note that a just-triggered broadcast is NOT settled even
     * though no run is in flight — the row appears only once a capture worker picks
     * the job up, and until then the button must stay disabled.
     */
    const settled = (run: BroadcastRun | null): boolean => {
      if (!pending) return !runInFlight(run);
      const appeared = run !== null && run.id !== pending.afterRunId;
      if (!appeared && Date.now() - pending.since < RUN_PENDING_GRACE_MS) return false;
      if (!cancelled) setPending(null);
      return !runInFlight(run);
    };

    load()
      .then((latest) => {
        if (cancelled || settled(latest)) return;
        timer = setInterval(
          () => {
            load()
              .then((r) => {
                if (settled(r)) stop();
              })
              .catch(() => undefined);
          },
          pending ? PENDING_POLL_MS : RUN_POLL_MS,
        );
      })
      .catch(() => {
        /* status cell is best-effort */
      });

    return () => {
      cancelled = true;
      stop();
    };
  }, [broadcast.id, pending]);

  // Prefer the actual posted Slack permalink from the latest run so "View in
  // Slack" opens the exact message (app_redirect is unreliable → glitch page).
  const slackOpenUrl = (latestRun?.deliveries ?? []).find(
    (d) => d.channelType === "slack" && isHttpUrl(d.providerMessageId),
  )?.providerMessageId;

  const status = summarizeRun(latestRun);
  const schedule = scheduleParts(broadcast.cron, broadcast.timezone);
  const meta = sourceMeta(broadcast);
  // A multi-source broadcast belongs to the workspace page: changing its schedule or channels from
  // inside one project would silently change what the other projects' owners receive, and
  // deleting it would take their broadcast away. Run now, pause and history stay available —
  // they act on the whole broadcast either way, and hiding them makes the tab feel broken.
  const sharedElsewhere = scope === "project" && sourceCount(broadcast) > 1;
  // Engine allows one in-flight run per broadcast; don't offer a click it will 409.
  const busy = runInFlight(latestRun) || pending !== null;

  const handleRunNow = async (): Promise<void> => {
    setRunning(true);
    // Disable on the click, not when the poll catches up: the run row is created only
    // when a worker picks the job up, and that gap was wide enough to click twice.
    setPending({ since: Date.now(), afterRunId: latestRun?.id ?? null });
    try {
      const accepted = await onRunNow(broadcast);
      if (!accepted) setPending(null);
    } finally {
      setRunning(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    setDeleting(true);
    try {
      await onDelete(broadcast);
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <TableRow data-testid="broadcast-row">
      {/* Name */}
      <TableCell className="max-w-[240px] py-3">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-semibold text-foreground">
            {broadcast.title}
          </span>
          {!broadcast.enabled &&
            (isAutoPaused(broadcast) ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    variant="outline"
                    className="shrink-0 text-[10px]"
                    data-testid="broadcast-auto-paused-badge"
                  >
                    Auto-paused
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  Paused automatically after repeated failed runs — fix the cause and
                  switch it back on.
                </TooltipContent>
              </Tooltip>
            ) : (
              <Badge variant="outline" className="shrink-0 text-[10px]">
                Paused
              </Badge>
            ))}
        </div>
        {isAutoPaused(broadcast) && broadcast.lastError && (
          // One line so the row never grows; hover shows the whole message, click opens
          // Run history where it is shown in full with Copy.
          <Tooltip delayDuration={200}>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => onOpenHistory(broadcast)}
                className="mt-0.5 block max-w-full truncate text-left text-xs text-destructive hover:underline"
                data-testid="broadcast-last-error"
              >
                {broadcast.lastError}
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-[360px] whitespace-normal">
              {broadcast.lastError}
              <div className="mt-1 text-[10px] opacity-70">Click for run history</div>
            </TooltipContent>
          </Tooltip>
        )}
        <div className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
          {meta.icon === "globe" ? (
            <Globe className="h-3 w-3 shrink-0" />
          ) : (
            <Layers className="h-3 w-3 shrink-0" />
          )}
          <span className="truncate">{meta.text}</span>
        </div>
      </TableCell>

      {/* Send to */}
      <TableCell className="py-3">
        <ChannelChips channels={broadcast.channels} />
      </TableCell>

      {/* Schedule */}
      <TableCell className="whitespace-nowrap py-3">
        <div className="text-sm tabular-nums text-foreground">{schedule.line}</div>
        <div className="mt-0.5 text-xs text-muted-foreground">{schedule.zone}</div>
      </TableCell>

      {/* Last run — the whole cell opens Run history */}
      <TableCell className="whitespace-nowrap py-3">
        <button
          type="button"
          onClick={() => onOpenHistory(broadcast)}
          className="group/status inline-flex max-w-[260px] items-center gap-2 text-left text-sm"
          title="Open run history"
        >
          {status.tone === "capturing" ? (
            <Loader2 className="h-3 w-3 shrink-0 animate-spin text-primary" />
          ) : (
            <span
              className={cn("h-1.5 w-1.5 shrink-0 rounded-full", TONE_DOT[status.tone])}
            />
          )}
          <span
            className={cn(
              "font-medium group-hover/status:underline",
              TONE_TEXT[status.tone],
            )}
          >
            {status.label}
          </span>
          {status.detail && (
            <span className="truncate text-xs text-muted-foreground tabular-nums">
              · {status.detail}
            </span>
          )}
        </button>
        {slackOpenUrl && (
          <Tooltip delayDuration={200}>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(slackOpenUrl, "_blank", "noopener,noreferrer");
                }}
                aria-label="View in Slack"
                className="ml-1.5 inline-flex align-middle text-primary hover:opacity-80"
              >
                <ExternalLink className="h-3 w-3" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top">View in Slack</TooltipContent>
          </Tooltip>
        )}
      </TableCell>

      {/* Active */}
      <TableCell className="sticky right-[56px] z-10 bg-card py-3">
        <Tooltip delayDuration={300}>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <Switch
                checked={broadcast.enabled}
                onCheckedChange={() => void onTogglePause(broadcast)}
                aria-label={broadcast.enabled ? "Pause broadcast" : "Resume broadcast"}
              />
            </span>
          </TooltipTrigger>
          <TooltipContent side="top">
            {broadcast.enabled ? "Pauses scheduled sends" : "Resumes scheduled sends"}
          </TooltipContent>
        </Tooltip>
      </TableCell>

      {/* Actions — a single kebab, always visible; items and their labels mirror
          SCIO3's broadcast row menu: Run now · Edit · Duplicate · View run history · Delete. */}
      <TableCell className="sticky right-0 z-10 w-[56px] bg-card py-3">
        <div className="flex items-center justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                data-testid="broadcast-actions-menu"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                data-testid="broadcast-run-now"
                onClick={() => void handleRunNow()}
                disabled={running || busy}
                title={
                  busy
                    ? "A run is already in progress — wait for it to finish"
                    : undefined
                }
              >
                <Play className="mr-2 h-4 w-4" />
                {busy ? "Run in progress…" : "Run now"}
              </DropdownMenuItem>
              {sharedElsewhere ? (
                <DropdownMenuItem asChild>
                  <a href="/dashboard/settings/broadcasts">
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Edit in workspace Broadcast
                  </a>
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  data-testid="broadcast-edit-btn"
                  onClick={() => onEdit(broadcast)}
                >
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit
                </DropdownMenuItem>
              )}
              {/* A copy shares every source, so a broadcast that other projects rely on is only
                  duplicated where it is edited — in workspace Broadcast. */}
              {!sharedElsewhere && (
                <DropdownMenuItem
                  data-testid="broadcast-duplicate-btn"
                  onClick={() => void onDuplicate(broadcast)}
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Duplicate
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                data-testid="broadcast-history-btn"
                onClick={() => onOpenHistory(broadcast)}
              >
                <History className="mr-2 h-4 w-4" />
                View run history
              </DropdownMenuItem>
              {!sharedElsewhere && (
                <DropdownMenuItem
                  className="text-red-600 focus:text-red-600"
                  data-testid="broadcast-delete-btn"
                  onClick={() => setConfirmDelete(true)}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this broadcast?</AlertDialogTitle>
              <AlertDialogDescription>
                “{broadcast.title}” and its schedule will be removed. This can’t be
                undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  void handleDelete();
                }}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </TableCell>
    </TableRow>
  );
}
